import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnChanges,
  OnInit,
  SimpleChanges,
  input,
  output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, ParamMap, Params } from '@angular/router';
import { QueryParamSelectObjectFilterForm } from './query-param-select-object-filter.form';

@Component({
  selector: 'qaroni-query-param-select-object-filter',
  templateUrl: './query-param-select-object-filter.component.html',
  styleUrl: './query-param-select-object-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QueryParamSelectObjectFilterComponent<T>
  extends QueryParamSelectObjectFilterForm
  implements OnInit, OnChanges
{
  readonly label = input<string>('');
  readonly firstOption = input<string>($localize`Todos`);
  readonly firstOptionValue = input<string>('');
  readonly showFirstOption = input<boolean>(true);
  readonly options = input.required<T[]>();
  readonly valueKey = input.required<keyof T | null>();
  readonly textKey = input.required<keyof T | null>();
  readonly queryParamName = input.required<string>();
  readonly multiple = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  readonly showTextFilter = input<boolean>(false);
  readonly searchKey = input<keyof T | null>(null);
  readonly searchPlaceholder = input<string>($localize`Buscar`);

  readonly queryParamsChange = output<Params>();

  private lastSelection: unknown[] = [];
  public searchControl = new FormControl<string>('');
  public filteredOptions: T[] = [];

  private route = inject(ActivatedRoute);

  private queryParamMap$ = this.route.queryParamMap;
  private valueChanges$ = this.searchControl.valueChanges;

  ngOnInit(): void {
    this.subs.add(this.valueChanges$.subscribe(this.getSearchValueChanges));
    if (!this.hasQueryParamName) {
      return;
    }

    this.subs.add(this.queryParamMap$.subscribe(this.getQueryParamMap));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']?.currentValue) {
      this.filterValueOptions(this.searchControl.value);
    }

    if (changes['disabled']) {
      this.toggleDisabled(this.disabled());
    }
  }

  get hasQueryParamName(): boolean {
    return this.queryParamName().trim().length > 0;
  }

  get hasConfiguration(): boolean {
    return this.hasQueryParamName && !!this.textKey() && !!this.valueKey();
  }

  get hasLabel(): boolean {
    return this.label().trim().length > 0;
  }

  public getValue(item: T) {
    const valueKey = this.valueKey();
    return valueKey ? item[valueKey] : item;
  }

  public getText(item: T) {
    const textKey = this.textKey();
    return textKey ? String(item[textKey]) : String(item);
  }

  public onchange(event: MatSelectChange): void {
    let currentSelectedValue = event.value;
    let valueToSend = '';

    const queryParamsEmit = { ...this.route.snapshot.queryParams };

    if (this.multiple()) {
      const wasAllSelected = this.lastSelection.includes(
        this.firstOptionValue()
      );
      const isAllSelected = currentSelectedValue.includes(
        this.firstOptionValue()
      );

      if (wasAllSelected && currentSelectedValue.length > 1) {
        currentSelectedValue = currentSelectedValue.filter(
          (v: unknown) => v !== this.firstOptionValue()
        );
      } else if (isAllSelected || currentSelectedValue.length === 0) {
        currentSelectedValue = [this.firstOptionValue()];
        this.searchControl.setValue('');
      }

      const isOnlyAllSelected =
        currentSelectedValue.length === 1 &&
        currentSelectedValue[0] === this.firstOptionValue();

      if (!isOnlyAllSelected) {
        valueToSend = currentSelectedValue.join(',');
      }

      this.paramValue.setValue(currentSelectedValue);
      this.lastSelection = currentSelectedValue;
    } else {
      if (currentSelectedValue !== this.firstOptionValue()) {
        valueToSend = currentSelectedValue;
      }
    }

    if (valueToSend !== '') {
      queryParamsEmit[this.queryParamName()] = valueToSend;
    } else {
      delete queryParamsEmit[this.queryParamName()];
    }

    this.queryParamsChange.emit(queryParamsEmit);
  }

  public onOpenedChange(opened: boolean): void {
    if (!opened) {
      this.searchControl.setValue('');
    }
  }

  private filterValueOptions = (searchValue: string) => {
    let currentSelectedValues: unknown[] = [];
    const rawValue = this.paramValue.value;

    if (Array.isArray(rawValue)) {
      currentSelectedValues = rawValue;
    } else if (rawValue) {
      currentSelectedValues = [rawValue];
    }

    if (!searchValue) {
      this.filteredOptions = [...this.options()];
      return;
    }

    const searchKeyValue = this.searchKey() ?? this.textKey();
    if (!searchKeyValue) {
      this.filteredOptions = [...this.options()];
      return;
    }

    const searchLower = searchValue.toLowerCase();

    this.filteredOptions = this.options().filter(item => {
      const propertyValue = item[searchKeyValue] as unknown as string;
      const matchesSearch = String(propertyValue)
        .toLowerCase()
        .includes(searchLower);
      const itemValue = this.getValue(item);
      const isSelected = currentSelectedValues.includes(itemValue);
      return matchesSearch || isSelected;
    });
  };

  private getQueryParamMap = (queryParamMap: ParamMap) => {
    const paramValues = queryParamMap.get(this.queryParamName());

    if (this.multiple()) {
      const values = paramValues
        ? paramValues.split(',')
        : [this.firstOptionValue()];
      this.paramValue.setValue(values);
      this.lastSelection = values;
      this.paramValueText = values;
    } else {
      if (paramValues) {
        this.paramValueText = paramValues;
      } else {
        this.resetQueryParamName();
      }
    }
  };

  private toggleDisabled(disabled: boolean): void {
    if (disabled) {
      this.paramValue.disable({ emitEvent: false });
    } else {
      this.paramValue.enable({ emitEvent: false });
    }
  }

  private getSearchValueChanges = (searchValue: string) => {
    this.filterValueOptions(searchValue);
  };
}
